import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { eventsAPI } from '@/services/api'

export const useEventsStore = defineStore('events', () => {
  // State
  const events = ref([])
  const currentEvent = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const pagination = ref({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  })
  const filters = ref({
    sport_key: null,
    team: null,
    date_from: null,
    date_to: null
  })

  // Getters
  const hasEvents = computed(() => events.value.length > 0)
  const isLoading = computed(() => loading.value)
  const hasError = computed(() => !!error.value)
  const totalEvents = computed(() => pagination.value.total)

  // Actions
  async function fetchEvents(params = {}) {
    loading.value = true
    error.value = null
    
    try {
      // Clean filters - remove null/undefined values
      const cleanFilters = Object.fromEntries(
        Object.entries(filters.value).filter(([key, value]) => value !== null && value !== undefined && value !== '')
      )
      
      const requestParams = {
        ...cleanFilters,
        ...params
      }
      
      const response = await eventsAPI.getEvents(requestParams)
      
      if (response.success) {
        events.value = response.data || []
        pagination.value = response.pagination || pagination.value
      } else {
        throw new Error(response.message || 'Failed to fetch events')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error fetching events:', err)
    } finally {
      loading.value = false
    }
  }

  async function fetchEvent(id) {
    loading.value = true
    error.value = null
    
    try {
      const response = await eventsAPI.getEvent(id)
      
      if (response.success) {
        // Handle new API structure: { event: {...}, user_bets: [...] }
        if (response.data.event) {
          currentEvent.value = response.data.event
          currentEvent.value.user_bets = response.data.user_bets || []
        } else {
          // Fallback for old API structure
          currentEvent.value = response.data
        }
      } else {
        throw new Error(response.message || 'Failed to fetch event')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error fetching event:', err)
    } finally {
      loading.value = false
    }
  }

  async function fetchUpcomingEvents(params = {}) {
    loading.value = true
    error.value = null
    
    try {
      const response = await eventsAPI.getUpcomingEvents(params)
      
      if (response.success) {
        events.value = response.data
      } else {
        throw new Error(response.message || 'Failed to fetch upcoming events')
      }
    } catch (err) {
      error.value = err.message
      console.error('Error fetching upcoming events:', err)
    } finally {
      loading.value = false
    }
  }

  function setFilters(newFilters) {
    filters.value = { ...filters.value, ...newFilters }
  }

  function clearFilters() {
    filters.value = {
      sport_key: null,
      team: null,
      date_from: null,
      date_to: null
    }
  }

  function clearError() {
    error.value = null
  }

  function clearEvents() {
    events.value = []
    currentEvent.value = null
    pagination.value = {
      current_page: 1,
      last_page: 1,
      per_page: 15,
      total: 0
    }
  }

  return {
    // State
    events,
    currentEvent,
    loading,
    error,
    pagination,
    filters,
    
    // Getters
    hasEvents,
    isLoading,
    hasError,
    totalEvents,
    
    // Actions
    fetchEvents,
    fetchEvent,
    fetchUpcomingEvents,
    setFilters,
    clearFilters,
    clearError,
    clearEvents,
  }
})